import * as React from 'react';
import { useConvex, useMutation } from 'convex/react';
import { api, type Id } from '@/convex';

export function maskApiKey(
  apiKey: string | null,
  visiblePrefix = 11,
): string | null {
  if (!apiKey) return null;
  return `${apiKey.slice(0, visiblePrefix)}${'*'.repeat(
    Math.max(0, apiKey.length - visiblePrefix),
  )}`;
}

export function useOrgApiKey({
  orgId,
  enabled,
}: {
  orgId: Id<'orgs'> | undefined;
  enabled: boolean;
}) {
  const convex = useConvex();
  const rotateApiKey = useMutation(api.orgs.rotateApiKey);
  const [apiKey, setApiKey] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [rotating, setRotating] = React.useState(false);

  React.useEffect(() => {
    setApiKey(null);
    if (!orgId || !enabled) return;

    let cancelled = false;
    setLoading(true);
    Promise.resolve(convex.query(api.orgs.revealApiKey, { orgId }))
      .then((result) => {
        if (!cancelled) setApiKey(result.apiKey);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [convex, enabled, orgId]);

  const rotate = React.useCallback(async () => {
    if (!orgId) return null;
    setRotating(true);
    try {
      const result = await rotateApiKey({ orgId });
      setApiKey(result.apiKey);
      return result.apiKey;
    } finally {
      setRotating(false);
    }
  }, [orgId, rotateApiKey]);

  return { apiKey, loading, rotating, rotate, setApiKey };
}
